import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  CircularProgress,
  Tooltip,
  TablePagination,
} from '@mui/material';
import {
  Add,
  Delete,
  AccountBalance,
  TrendingUp,
  TrendingDown,
  Refresh,
} from '@mui/icons-material';
import { accountBalancesApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { formatCurrency, formatDate } from '../utils/helpers';

/**
 * Format a TIME string (HH:MM:SS or HH:MM) for display.
 * Returns empty string if falsy.
 */
const formatTime = (time) => {
  if (!time) return '';
  // time may be "HH:MM:SS" or "HH:MM" — show HH:MM
  const parts = time.split(':');
  if (parts.length >= 2) {
    const h = parseInt(parts[0], 10);
    const m = parts[1];
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  }
  return time;
};

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

// A balance older than this is shown as out of date
const STALE_DAYS = 31;

const BalanceManager = () => {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [latestBalances, setLatestBalances] = useState([]);
  const [allBalances, setAllBalances] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selectedAccount, setSelectedAccount] = useState(null); // { account_id, bank }
  const [addDialog, setAddDialog] = useState(false);
  const [form, setForm] = useState({
    accountId: '',
    bank: '',
    accountType: 'bank_account',
    balance: '',
    balanceDate: new Date().toISOString().split('T')[0],
  });

  const loadLatest = useCallback(async () => {
    try {
      const res = await accountBalancesApi.getLatest();
      setLatestBalances(res.data?.balances || []);
    } catch (error) {
      console.error('Error loading latest balances:', error);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      if (selectedAccount) {
        if (selectedAccount.account_id) params.account_id = selectedAccount.account_id;
        if (selectedAccount.bank) params.bank = selectedAccount.bank;
      }
      const res = await accountBalancesApi.getAll(params);
      setAllBalances(res.data?.balances || []);
      setTotalCount(res.data?.total || 0);
    } catch (error) {
      console.error('Error loading balance history:', error);
      showError('Failed to load balance data');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, selectedAccount, showError]);

  useEffect(() => {
    loadLatest();
  }, [loadLatest]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleRefresh = () => {
    loadLatest();
    loadHistory();
  };

  const handleAccountSelect = (bal) => {
    const isSame =
      selectedAccount?.account_id === bal.account_id &&
      selectedAccount?.bank === bal.bank;
    if (isSame) {
      // Deselect
      setSelectedAccount(null);
    } else {
      setSelectedAccount({ account_id: bal.account_id, bank: bal.bank });
    }
    setPage(0); // reset to first page on filter change
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAdd = async () => {
    try {
      if (!form.balance || !form.balanceDate) {
        showError('Balance amount and date are required');
        return;
      }
      await accountBalancesApi.create({
        accountId: form.accountId || null,
        bank: form.bank || null,
        accountType: form.accountType,
        balance: parseFloat(form.balance),
        balanceDate: form.balanceDate,
      });
      showSuccess('Balance record added');
      setAddDialog(false);
      setForm({
        accountId: '',
        bank: '',
        accountType: 'bank_account',
        balance: '',
        balanceDate: new Date().toISOString().split('T')[0],
      });
      handleRefresh();
    } catch (error) {
      showError('Failed to add balance: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (id) => {
    try {
      await accountBalancesApi.delete(id);
      showSuccess('Balance record deleted');
      handleRefresh();
    } catch (error) {
      showError('Failed to delete balance record');
    }
  };

  return (
    <Box>
      {/* Latest Balances Summary */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>
          Account Balances
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => setAddDialog(true)}
          >
            Add Balance
          </Button>
        </Stack>
      </Box>

      {latestBalances.length > 0 ? (
        <>
          {[
            { title: 'Bank accounts', rows: latestBalances.filter(b => b.account_type !== 'credit_card'), isCard: false },
            // Card alerts state the available limit, not money you have
            { title: 'Credit cards · available limit', rows: latestBalances.filter(b => b.account_type === 'credit_card'), isCard: true },
          ].filter(section => section.rows.length > 0).map(section => (
            <Box key={section.title} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {section.title}
              </Typography>
              <Grid container spacing={2}>
                {section.rows.map((bal) => {
                  const isSelected =
                    selectedAccount?.account_id === bal.account_id &&
                    selectedAccount?.bank === bal.bank;
                  const amount = parseFloat(bal.balance);
                  const stale = Date.now() - new Date(bal.balance_date).getTime() > STALE_DAYS * 86400000;
                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={bal.id}>
                      <Card
                        onClick={() => handleAccountSelect(bal)}
                        sx={{
                          borderRadius: '12px',
                          border: isSelected
                            ? '2px solid'
                            : '1px solid rgba(0,0,0,0.08)',
                          borderColor: isSelected ? 'primary.main' : undefined,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
                          ...(isSelected && {
                            bgcolor: 'action.selected',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                          }),
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {bal.account_id
                                  ? `${section.isCard ? 'Card' : 'Account'} xx${bal.account_id}`
                                  : `${bal.bank || 'Unknown'} ${section.isCard ? 'card' : 'account'} (no number)`}
                              </Typography>
                              {bal.bank && bal.account_id && (
                                <Typography variant="caption" color="text.secondary">
                                  {bal.bank}
                                </Typography>
                              )}
                            </Box>
                            <Chip
                              label={bal.source || 'manual'}
                              size="small"
                              variant="outlined"
                              color={
                                bal.source === 'manual'
                                  ? 'primary'
                                  : bal.source === 'email'
                                    ? 'info'
                                    : 'secondary'
                              }
                            />
                          </Box>
                          {section.isCard ? (
                            <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }} color={stale ? 'text.secondary' : 'text.primary'}>
                              {formatCurrency(Math.abs(amount))}
                            </Typography>
                          ) : (
                            <Typography
                              variant="h5"
                              fontWeight={700}
                              sx={{
                                mt: 1,
                                color: stale ? 'text.secondary' : amount >= 0 ? 'success.main' : 'error.main',
                              }}
                            >
                              {amount >= 0 ? (
                                <TrendingUp sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom' }} />
                              ) : (
                                <TrendingDown sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom' }} />
                              )}
                              {formatCurrency(Math.abs(amount))}
                            </Typography>
                          )}
                          <Typography variant="caption" color={stale ? 'warning.main' : 'text.secondary'}>
                            {section.isCard ? 'Available limit as of ' : 'as of '}{formatDate(bal.balance_date)}
                            {bal.balance_time ? ` at ${formatTime(bal.balance_time)}` : ''}
                            {stale ? ' · no update in over a month' : ''}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ))}
        </>
      ) : (
        <Alert severity="info" sx={{ mb: 3, borderRadius: '8px' }}>
          No balance records yet. Add a balance manually or process bank statements to auto-detect balances.
        </Alert>
      )}

      {/* Filter indicator */}
      {selectedAccount && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={`Filtered: ${selectedAccount.bank || ''} ${selectedAccount.account_id || 'Unknown'}`}
            onDelete={() => { setSelectedAccount(null); setPage(0); }}
            color="primary"
            size="small"
          />
        </Box>
      )}

      {/* History Table */}
      <Card sx={{ borderRadius: '12px', border: '1px solid rgba(0,0,0,0.08)' }}>
        <CardHeader
          title="Balance History"
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : allBalances.length > 0 ? (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Account</TableCell>
                      <TableCell>Bank</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allBalances.map((bal) => (
                      <TableRow key={bal.id} hover>
                        <TableCell>{bal.accountId || '—'}</TableCell>
                        <TableCell>{bal.bank || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            label={bal.accountType === 'credit_card' ? 'Credit Card' : 'Bank Account'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 600,
                            color: parseFloat(bal.balance) >= 0 ? 'success.main' : 'error.main',
                          }}
                        >
                          {formatCurrency(parseFloat(bal.balance))}
                        </TableCell>
                        <TableCell>{formatDate(bal.balanceDate)}</TableCell>
                        <TableCell>
                          {bal.balanceTime ? formatTime(bal.balanceTime) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={bal.source || 'manual'}
                            size="small"
                            color={
                              bal.source === 'manual'
                                ? 'primary'
                                : bal.source === 'email'
                                  ? 'info'
                                  : 'secondary'
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(bal.id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              />
            </>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {selectedAccount
                  ? 'No balance records for this account'
                  : 'No balance records found'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add Balance Dialog */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Balance Record</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Account ID"
              value={form.accountId}
              onChange={(e) => setForm({ ...form, accountId: e.target.value })}
              placeholder="e.g., XXXX1234"
              size="small"
              fullWidth
            />
            <TextField
              label="Bank Name"
              value={form.bank}
              onChange={(e) => setForm({ ...form, bank: e.target.value })}
              placeholder="e.g., HDFC Bank"
              size="small"
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Account Type</InputLabel>
              <Select
                value={form.accountType}
                onChange={(e) => setForm({ ...form, accountType: e.target.value })}
                label="Account Type"
              >
                <MenuItem value="bank_account">Bank Account</MenuItem>
                <MenuItem value="credit_card">Credit Card</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Balance Amount"
              type="number"
              value={form.balance}
              onChange={(e) => setForm({ ...form, balance: e.target.value })}
              required
              size="small"
              fullWidth
              helperText="Use negative values for credit card dues"
            />
            <TextField
              label="Balance Date"
              type="date"
              value={form.balanceDate}
              onChange={(e) => setForm({ ...form, balanceDate: e.target.value })}
              required
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}>
            Add Balance
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BalanceManager;
